import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Heart,
  User,
  Mail,
  Phone,
  MapPin,
  Clock,
  RefreshCw,
  Check,
  X,
  Trash2,
  Search,
  Shield,
  AlertCircle,
  Calendar,
  Briefcase
} from 'lucide-react';

const AdminRegistrations = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (userTypeFilter !== 'all') params.user_type = userTypeFilter;
      
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { params, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/registrations/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter, userTypeFilter, token]);

  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await axios.patch(`${API}/admin/registrations/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User approved successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      await axios.patch(
        `${API}/admin/registrations/${selectedUser.id}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User rejected');
      setShowRejectDialog(false);
      setSelectedUser(null);
      setRejectReason('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to permanently delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/registrations/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-500 text-white">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUserTypeBadge = (type) => {
    if (type === 'volunteer') {
      return <Badge className="bg-plp-gold text-plp-blue"><Heart className="w-3 h-3 mr-1" />Volunteer</Badge>;
    }
    return <Badge variant="outline"><User className="w-3 h-3 mr-1" />Member</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-plp-blue" /> User Registrations
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Review and approve new community members</p>
        </div>
        <Button variant="outline" onClick={fetchUsers} data-testid="refresh-registrations">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-amber-500' : ''}`} onClick={() => setFilter('pending')}>
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold text-amber-600">{stats.pending || 0}</p>
            <p className="text-xs text-slate-500">Pending Approval</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-emerald-500' : ''}`} onClick={() => setFilter('approved')}>
          <CardContent className="p-4 text-center">
            <Check className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-600">{stats.approved || 0}</p>
            <p className="text-xs text-slate-500">Approved</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${userTypeFilter === 'member' ? 'ring-2 ring-plp-blue' : ''}`} onClick={() => { setUserTypeFilter('member'); setFilter('all'); }}>
          <CardContent className="p-4 text-center">
            <User className="w-6 h-6 mx-auto mb-2 text-plp-blue" />
            <p className="text-2xl font-bold">{stats.members || 0}</p>
            <p className="text-xs text-slate-500">Members</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${userTypeFilter === 'volunteer' ? 'ring-2 ring-plp-gold' : ''}`} onClick={() => { setUserTypeFilter('volunteer'); setFilter('all'); }}>
          <CardContent className="p-4 text-center">
            <Heart className="w-6 h-6 mx-auto mb-2 text-plp-gold" />
            <p className="text-2xl font-bold">{stats.volunteers || 0}</p>
            <p className="text-xs text-slate-500">Volunteers</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Alert */}
      {stats.pending > 0 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <div>
            <p className="font-medium text-amber-800">
              {stats.pending} registration{stats.pending > 1 ? 's' : ''} awaiting approval
            </p>
            <p className="text-sm text-amber-600">
              {stats.pending_volunteers > 0 && `${stats.pending_volunteers} volunteer${stats.pending_volunteers > 1 ? 's' : ''}`}
              {stats.pending_volunteers > 0 && stats.pending_members > 0 && ' and '}
              {stats.pending_members > 0 && `${stats.pending_members} member${stats.pending_members > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Tabs value={filter} onValueChange={(v) => { setFilter(v); setUserTypeFilter('all'); }} className="w-full sm:w-auto">
          <TabsList className="flex-wrap h-auto p-1">
            <TabsTrigger value="pending" data-testid="filter-pending" className="text-xs sm:text-sm">
              <Clock className="w-4 h-4 mr-1" /> Pending
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="filter-approved" className="text-xs sm:text-sm">
              <Check className="w-4 h-4 mr-1" /> Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="filter-rejected" className="text-xs sm:text-sm">
              <X className="w-4 h-4 mr-1" /> Rejected
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-all" className="text-xs sm:text-sm">
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-registrations"
          />
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="bg-white" data-testid={`user-card-${user.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      user.user_type === 'volunteer' ? 'bg-plp-gold/20' : 'bg-plp-blue/10'
                    }`}>
                      {user.user_type === 'volunteer' ? (
                        <Heart className="w-6 h-6 text-plp-gold" />
                      ) : (
                        <User className="w-6 h-6 text-plp-blue" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-heading font-bold text-slate-900">{user.name}</h3>
                        {getUserTypeBadge(user.user_type)}
                        {getStatusBadge(user.status || 'approved')}
                      </div>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{user.email}</span>
                        </p>
                        {user.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            {user.phone}
                          </p>
                        )}
                        {user.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {user.address}
                          </p>
                        )}
                        <p className="flex items-center gap-2 text-xs text-slate-400">
                          <Calendar className="w-3 h-3" />
                          Registered: {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Volunteer Details */}
                      {user.user_type === 'volunteer' && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          {user.availability && (
                            <p className="text-sm flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">{user.availability}</span>
                            </p>
                          )}
                          {user.skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {user.skills.map((skill) => (
                                <Badge key={skill} variant="outline" className="text-xs">
                                  <Briefcase className="w-3 h-3 mr-1" />
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {user.interests?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {user.interests.map((interest) => (
                                <Badge key={interest} className="bg-plp-gold/20 text-plp-blue text-xs">
                                  {interest}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Rejection Reason */}
                      {user.status === 'rejected' && user.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                          <strong>Rejection reason:</strong> {user.rejection_reason}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex sm:flex-col gap-2">
                    {(user.status === 'pending' || !user.status) && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(user.id)}
                          disabled={processing}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid={`approve-${user.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedUser(user); setShowRejectDialog(true); }}
                          disabled={processing}
                          className="text-red-500 border-red-200 hover:bg-red-50"
                          data-testid={`reject-${user.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {user.status === 'rejected' && (
                      <Button
                        size="sm"
                        onClick={() => handleApprove(user.id)}
                        disabled={processing}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(user.id)}
                      className="text-red-500 hover:bg-red-50"
                      data-testid={`delete-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" /> Reject Registration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedUser?.name}</p>
              <p className="text-sm text-slate-500">{selectedUser?.email}</p>
              <Badge className="mt-2">{selectedUser?.user_type === 'volunteer' ? 'Volunteer' : 'Member'}</Badge>
            </div>
            <div>
              <Label>Rejection Reason (optional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                rows={3}
                data-testid="reject-reason"
              />
              <p className="text-xs text-slate-500 mt-1">This will be sent to the user.</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowRejectDialog(false); setSelectedUser(null); setRejectReason(''); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-500 hover:bg-red-600"
                data-testid="confirm-reject"
              >
                {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                Reject User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRegistrations;
